/**
 * Floating TTS Toolbar
 *
 * Draggable toolbar for text-to-speech controls.
 * Appears when "Read Page" is enabled in Accessibility Settings.
 */

import { useState, useEffect, useRef } from 'react'
import { Play, Pause, SkipForward, SkipBack, GripVertical } from 'lucide-react'
import { useReadAloud } from '@/contexts/ReadAloudContext'
import { useTextToSpeech } from '@/contexts/TextToSpeechContext'
import { getUserAccessibilitySettings } from '@lina/accessibility/user'
import { cn } from '@/lib/utils'

interface FloatingTTSToolbarProps {
  className?: string
}

interface Position {
  x: number
  y: number
}

const POSITION_COOKIE_NAME = 'floatingTTS_position'

function getStoredPosition(): Position | null {
  if (typeof document === 'undefined') return null

  const cookies = document.cookie.split(';')
  const positionCookie = cookies.find((c) => c.trim().startsWith(`${POSITION_COOKIE_NAME}=`))

  if (positionCookie) {
    try {
      const value = positionCookie.split('=')[1]
      return JSON.parse(decodeURIComponent(value))
    } catch {
      return null
    }
  }

  return null
}

function savePosition(position: Position) {
  if (typeof document === 'undefined') return

  const expires = new Date()
  expires.setFullYear(expires.getFullYear() + 1)
  document.cookie = `${POSITION_COOKIE_NAME}=${encodeURIComponent(
    JSON.stringify(position),
  )}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`
}

function getDefaultPosition(): Position {
  if (typeof window === 'undefined') return { x: 0, y: 0 }
  return {
    x: (window.innerWidth - 400) / 2,
    y: window.innerHeight - 150,
  }
}

export function FloatingTTSToolbar({ className }: FloatingTTSToolbarProps) {
  const {
    isReadingPage,
    currentReadableIndex,
    readableElements,
    startPageNarration,
    pausePageNarration,
    resumePageNarration,
    skipToNext,
    skipToPrevious,
  } = useReadAloud()

  const { isPaused } = useTextToSpeech()

  const [position, setPosition] = useState<Position>(
    () => getStoredPosition() || getDefaultPosition(),
  )
  const [isDragging, setIsDragging] = useState(false)
  const [dragOffset, setDragOffset] = useState<Position>({ x: 0, y: 0 })
  const [showToolbar, setShowToolbar] = useState(
    () => getUserAccessibilitySettings().showTTSToolbar,
  )
  const toolbarRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const checkSettings = () => {
      const settings = getUserAccessibilitySettings()
      setShowToolbar(settings.showTTSToolbar)
    }
    const interval = setInterval(checkSettings, 500)
    window.addEventListener('storage', checkSettings)
    return () => {
      clearInterval(interval)
      window.removeEventListener('storage', checkSettings)
    }
  }, [])

  const handlePlayPause = () => {
    if (!isReadingPage) {
      startPageNarration()
    } else if (isPaused) {
      resumePageNarration()
    } else {
      pausePageNarration()
    }
  }

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!toolbarRef.current) return
    setIsDragging(true)
    const rect = toolbarRef.current.getBoundingClientRect()
    setDragOffset({ x: e.clientX - rect.left, y: e.clientY - rect.top })
    e.preventDefault()
  }

  useEffect(() => {
    if (!isDragging) return
    const handleMouseMove = (e: MouseEvent) => {
      const newPosition = { x: e.clientX - dragOffset.x, y: e.clientY - dragOffset.y }
      const maxX = window.innerWidth - (toolbarRef.current?.offsetWidth || 400)
      const maxY = window.innerHeight - (toolbarRef.current?.offsetHeight || 60)
      newPosition.x = Math.max(0, Math.min(newPosition.x, maxX))
      newPosition.y = Math.max(0, Math.min(newPosition.y, maxY))
      setPosition(newPosition)
    }
    const handleMouseUp = () => {
      setIsDragging(false)
      savePosition(position)
    }
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isDragging, dragOffset, position])

  const handleTouchStart = (e: React.TouchEvent) => {
    if (!toolbarRef.current) return
    setIsDragging(true)
    const rect = toolbarRef.current.getBoundingClientRect()
    const touch = e.touches[0]
    setDragOffset({ x: touch.clientX - rect.left, y: touch.clientY - rect.top })
    e.preventDefault()
  }

  useEffect(() => {
    if (!isDragging) return
    const handleTouchMove = (e: TouchEvent) => {
      const touch = e.touches[0]
      const newPosition = { x: touch.clientX - dragOffset.x, y: touch.clientY - dragOffset.y }
      const maxX = window.innerWidth - (toolbarRef.current?.offsetWidth || 400)
      const maxY = window.innerHeight - (toolbarRef.current?.offsetHeight || 60)
      newPosition.x = Math.max(0, Math.min(newPosition.x, maxX))
      newPosition.y = Math.max(0, Math.min(newPosition.y, maxY))
      setPosition(newPosition)
    }
    const handleTouchEnd = () => {
      setIsDragging(false)
      savePosition(position)
    }
    document.addEventListener('touchmove', handleTouchMove)
    document.addEventListener('touchend', handleTouchEnd)
    return () => {
      document.removeEventListener('touchmove', handleTouchMove)
      document.removeEventListener('touchend', handleTouchEnd)
    }
  }, [isDragging, dragOffset, position])

  if (!showToolbar) return null

  return (
    <div
      ref={toolbarRef}
      className={cn(
        'fixed z-50 glass-subtle rounded-lg',
        'flex flex-col gap-2 p-3',
        isDragging && 'shadow-2xl',
        className,
      )}
      style={{ left: `${position.x}px`, top: `${position.y}px` }}
      role="toolbar"
      aria-label="Text-to-speech playback controls"
      data-skip-read-aloud="true"
    >
      <span
        className={cn(
          'text-xs text-muted-foreground text-center text-medium',
          isDragging ? 'cursor-grabbing' : 'cursor-grab',
        )}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        title="Drag to move"
      >
        Text-to-Speech Controls
      </span>
      <div className="glass-subtle rounded-xl p-2 flex items-center gap-3">
        <div
          className={cn(
            'flex flex-col items-center text-muted-foreground hover:text-foreground transition-colors',
            isDragging ? 'cursor-grabbing' : 'cursor-grab',
          )}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
          title="Drag to move"
        >
          <GripVertical className="h-5 w-5" />
          <span className="h-[14px]" />
        </div>

        <div className="border-l h-6 border-border/50" />

        <button
          onClick={skipToPrevious}
          disabled={!isReadingPage || currentReadableIndex === 0}
          className={cn(
            'flex flex-col items-center gap-2 p-2 rounded transition-all',
            'focus:outline-none focus:ring-2 focus:ring-primary',
          )}
          aria-label="Skip to previous section"
          title="Previous (F7)"
        >
          <SkipBack
            className={cn('h-5 w-5 transition-all', 'hover:text-primary hover:scale-110')}
          />
          <span className="text-xs text-muted-foreground font-medium">Back</span>
        </button>

        <button
          onClick={handlePlayPause}
          className={cn('flex flex-col items-center gap-2 p-2 rounded transition-all')}
          aria-label={
            !isReadingPage ? 'Start reading page' : isPaused ? 'Resume reading' : 'Pause reading'
          }
          title={!isReadingPage ? 'Play (F9)' : isPaused ? 'Resume (F9)' : 'Pause (F9)'}
        >
          {!isReadingPage || isPaused ? (
            <Play
              className={cn(
                'h-5 w-5 transition-all',
                'hover:scale-110',
                'focus:outline-none focus:ring-2 focus:ring-green-600 dark:focus:ring-green-400',
                isReadingPage && isPaused && 'hover:text-green-600 dark:hover:text-green-400',
              )}
            />
          ) : (
            <Pause
              className={cn(
                'h-5 w-5 transition-all',
                'hover:scale-110',
                'hover:text-orange-600 dark:hover:text-orange-400',
                'focus:outline-none focus:ring-2 focus:ring-orange-600 dark:focus:ring-orange-400',
              )}
            />
          )}
          <span className="text-xs text-muted-foreground font-medium">
            {!isReadingPage || isPaused ? 'Play' : 'Pause'}
          </span>
        </button>

        <button
          onClick={skipToNext}
          disabled={!isReadingPage || currentReadableIndex >= readableElements.length - 1}
          className={cn(
            'flex flex-col items-center gap-2 p-2 rounded transition-all',
            'focus:outline-none focus:ring-2 focus:ring-primary',
          )}
          aria-label="Skip to next section"
          title="Next (F8)"
        >
          <SkipForward
            className={cn('h-5 w-5 transition-all', 'hover:text-primary hover:scale-110')}
          />
          <span className="text-xs text-muted-foreground font-medium">Next</span>
        </button>
      </div>

      <div
        className={cn(
          'text-[9px] text-muted-foreground text-center max-w-[200px]',
          isDragging ? 'cursor-grabbing' : 'cursor-grab',
        )}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        title="Drag toolbar by clicking here or the grip icon"
      >
        💡 Drag by clicking here or the grip icon
      </div>
    </div>
  )
}
